import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { CSGHelper } from '../core/CSGHelper';
import { BIMElement } from '../core/types';
import { layerMaterialColor, MaterialProjectSlice } from './MaterialResolver';
import { buildOpeningCutter } from '../utils/structuralOpeningGeometry';
import { clipPolygonMaxV, shapeFromPolygon2D, wallBasisMatrix } from '../utils/wallProfileGeometry';
import { sanitizeWallProfileQuad } from '../utils/WallGeometryUtils';
import { useBIMStore } from '../state/useBIMStore';
import { BIMInstanceGroup } from './instancing/BIMInstancedCategory';

/**
 * Persistent wall body geometry cache (instant view-mode switching).
 *
 * Walls with hosted openings pay a real CSG subtract on every geometry build.
 * Switching between the instanced shaded-family view modes (shaded/rendered/
 * realistic/xray) and the edge-family modes (hiddenLine/wireframe/
 * consistentColors) unmounts the instanced batches and mounts per-element
 * renderers — previously rebuilding every wall's CSG in one synchronous
 * render pass, which froze the viewport for the whole switch.
 *
 * The cache keys results by the wall's `geometryData` object identity (the
 * kernel uses replace-on-write, so an unchanged wall keeps its identity) plus
 * the plan cut-plane height, and validates against the opening id lists.
 * Results survive unmount/remount cycles, so a view-mode swap re-attaches
 * already-built geometries instead of recomputing them.
 *
 * The cache owns its geometries: entries are disposed on eviction, on
 * overwrite (edited openings) and via `releaseWallGeometry` when a wall's
 * committed geometry is replaced. Renderers therefore must NOT dispose
 * cache-owned layers themselves (see WallRenderer).
 */

export interface WallLayerMesh {
    geometry: THREE.BufferGeometry;
    color: string;
    index: number;
}

/**
 * Result handed to renderers. `cacheOwned: false` marks an uncached build
 * (live drag preview) whose geometries the renderer must dispose itself.
 */
export interface WallLayersResult {
    layers: WallLayerMesh[];
    cacheOwned: boolean;
}

const EMPTY_OPENINGS: BIMElement[] = [];

// ---------------------------------------------------------------------------
// Host→opening indexes (moved from BIMSceneContent; shared with the warm-up)
// ---------------------------------------------------------------------------

export interface OpeningIndex {
    /** wall id -> Map<string, BIMElement[]> */
    hostedByWall: Map<string, BIMElement[]>;
    /** host element id -> Map<string, BIMElement[]> */
    byStructuralHost: Map<string, BIMElement[]>;
    /** shaft-category structural openings (pierce horizontal comps) */
    shafts: BIMElement[];
}

const openingIndexCache = new WeakMap<Map<string, BIMElement>, OpeningIndex>();

export function getOpeningIndex(elements: Map<string, BIMElement>): OpeningIndex {
    const cached = openingIndexCache.get(elements);
    if (cached) return cached;
    const hostedByWall = new Map<string, BIMElement[]>();
    const byStructuralHost = new Map<string, BIMElement[]>();
    const shafts: BIMElement[] = [];
    elements.forEach((e) => {
        if (e.category === 'Doors' || e.category === 'Windows') {
            const hostId = (e as any).geometryData?.hostWallId as string | undefined;
            if (hostId) {
                const list = hostedByWall.get(hostId);
                if (list) list.push(e);
                else hostedByWall.set(hostId, [e]);
            }
        } else if (e.category === 'Structural Openings') {
            const g = e.geometryData as any;
            if (!g) return;
            if (typeof g.hostId === 'string' && g.hostId) {
                const list = byStructuralHost.get(g.hostId);
                if (list) list.push(e);
                else byStructuralHost.set(g.hostId, [e]);
            }
            if (g.kind === 'Shaft') shafts.push(e);
        }
    });
    const index: OpeningIndex = { hostedByWall, byStructuralHost, shafts };
    openingIndexCache.set(elements, index);
    return index;
}

// ---------------------------------------------------------------------------
// Geometry builder (extracted verbatim from WallRenderer's layersMeshes memo)
// ---------------------------------------------------------------------------

/**
 * Builds the wall body layer meshes: sketched elevation profile, joined
 * multi-layer profiles, or the plain (optionally CSG-cut) box. All transforms
 * are baked into the geometry — the JSX consumes `layer.geometry` only.
 */
export function buildWallLayerMeshes(
    geometryData: any,
    openings: BIMElement[],
    parametricOpenings: BIMElement[],
    cutPlaneZ: number | null,
    project: MaterialProjectSlice,
): WallLayerMesh[] | null {
    if (!geometryData) return null;
    // Read-time guard: every branch below bakes transforms from the location
    // line, so a wall without one has no renderable body.
    if (!geometryData.start || !geometryData.end) return null;

    const start = new THREE.Vector3(...geometryData.start);
    const end = new THREE.Vector3(...geometryData.end);
    const length = start.distanceTo(end);
    const height = geometryData.height ?? 3;
    const thickness = geometryData.thickness ?? 0.2;
    const baseZ = geometryData.baseOffset || 0;
    // Rendered height: truncate at the cut plane (plan view cut)
    const renderHeight = cutPlaneZ !== null ? cutPlaneZ - baseZ : height;

    // Voids
    const openingMeshes = openings.map(hosted => {
        const hGeom = (hosted as any).geometryData;
        if (!hGeom) return null;

        const width = hGeom.width || 0.9;
        const hHeight = hGeom.height || 2.1;
        const sillHeight = hGeom.sillHeight || 0;
        const posParam = hGeom.position || 0.5;

        // Void spans from sill up to min(door top, rendered wall top):
        // when the wall is truncated at the cut plane, the void must not
        // poke above the truncation or the CSG re-caps the opening.
        const voidBottom = baseZ + sillHeight;
        const voidTop = Math.min(voidBottom + hHeight, baseZ + renderHeight);
        if (voidTop <= voidBottom) return null;

        const hPos = start.clone().lerp(end, posParam);
        hPos.z = (voidBottom + voidTop) / 2;

        const angle = Math.atan2(end.y - start.y, end.x - start.x);

        // Thicker than wall to ensure cut
        const box = new THREE.BoxGeometry(width, thickness + 0.5, voidTop - voidBottom);
        const mesh = new THREE.Mesh(box);
        mesh.position.copy(hPos);
        mesh.rotation.set(0, 0, angle);
        mesh.updateMatrix();
        return mesh;
    }).filter(m => m !== null) as THREE.Mesh[];

    // Parametric Structural Opening voids (Structure > Opening panel) cut
    // this wall in addition to hosted doors/windows. Sketched 'Profile'
    // voids get the wall frame so their (u, v) polygon prisms land on the
    // wall face; plan cut height truncates them like the wall body.
    const wallCtx = { start, end, baseZ, cutMaxV: cutPlaneZ !== null ? renderHeight : undefined };
    for (const opening of parametricOpenings) {
        const cutter = buildOpeningCutter(opening.geometryData as any, wallCtx);
        if (cutter) openingMeshes.push(cutter);
    }

    // 0. Sketched elevation profile (BIM "Edit Profile" wall shape):
    // sweep the custom (u, v) outline across the wall thickness. Takes
    // precedence over the plan-based join profiles.
    if (Array.isArray(geometryData.elevationProfile) && geometryData.elevationProfile.length >= 3) {
        let profilePoints = geometryData.elevationProfile as [number, number][];
        if (cutPlaneZ !== null) {
            profilePoints = clipPolygonMaxV(profilePoints, renderHeight);
        }
        if (profilePoints.length >= 3) {
            const shape = shapeFromPolygon2D(profilePoints);
            const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
            // Center the sweep on the location line, then place it through
            // the wall's local frame — every transform baked into the
            // geometry (CSGHelper consumes raw geometry only, see below).
            geo.translate(0, 0, -thickness / 2);
            geo.applyMatrix4(wallBasisMatrix({ start, end, baseZ }));

            const mesh = new THREE.Mesh(geo);
            mesh.updateMatrix();
            const resultMesh = CSGHelper.subtract(mesh, openingMeshes);

            // Single swept body: honor the core layer's material when one
            // exists, falling back like the legacy single-profile path.
            const coreLayer = (geometryData.layers ?? []).find((l: any) => l?.function === 'Structure')
                ?? geometryData.layers?.[0];
            const color = layerMaterialColor(project, coreLayer) ?? '#e0e0e0';
            return [{ geometry: resultMesh.geometry, color, index: 0 }];
        }
        // Clipped empty (profile sits entirely above the cut plane):
        // fall through to the legacy paths rather than vanishing in plan.
    }

    // 1. Multi-Layer Case
    if (geometryData.layerProfiles && geometryData.layerProfiles.length > 0) {
        return geometryData.layerProfiles.map((profile: [number, number][], index: number) => {
            // Read-time guard: a crossed layer footprint in stored data
            // rebuilds from the location line instead of rendering as a
            // butterfly; unusable layer polygons drop out entirely.
            const safeProfile = sanitizeWallProfileQuad(profile as number[][], geometryData.start, geometryData.end);
            if (!safeProfile) return null;
            const shape = new THREE.Shape();
            if (safeProfile.length > 0) {
                shape.moveTo(safeProfile[0][0], safeProfile[0][1]);
                for (let i = 1; i < safeProfile.length; i++) {
                    shape.lineTo(safeProfile[i][0], safeProfile[i][1]);
                }
                shape.closePath();
            }

            const geo = new THREE.ExtrudeGeometry(shape, { depth: renderHeight, bevelEnabled: false });

            // WallJoinSolver returns profile coords in World X, World Y and
            // ExtrudeGeometry extrudes along local Z, so the only transform
            // needed is the base offset. Bake it into the geometry instead of
            // a mesh transform: the JSX below consumes `resultMesh.geometry`
            // only, and CSGHelper.subtract() early-returns the untouched
            // target when a wall has no openings — a mesh transform would
            // then be silently discarded and an unjoined wall would render as
            // an unrotated box pinned to the world origin.
            geo.translate(0, 0, baseZ);

            const mesh = new THREE.Mesh(geo);
            mesh.updateMatrix();

            // Apply Cuts
            const cutMesh = CSGHelper.subtract(mesh, openingMeshes);

            // Determine Layer Material Color — registry first (BIM-like),
            // legacy per-function palette only as fallback.
            const layerParams = geometryData.layers ? geometryData.layers[index] : null;
            let color = layerMaterialColor(project, layerParams) ?? '#e0e0e0';
            if (!layerMaterialColor(project, layerParams) && layerParams) {
                switch (layerParams.function) {
                    case 'Structure': color = '#808080'; break; // Gray
                    case 'Substrate': color = '#a0a0a0'; break; // Light Gray
                    case 'Thermal/Air Layer': color = '#ffff00'; break; // Yellow
                    case 'Finish 1': color = '#f0f0f0'; break; // White
                    case 'Finish 2': color = '#d0d0d0'; break;
                    case 'Membrane Layer': color = '#000000'; break;
                }
            }

            return { geometry: cutMesh.geometry, color, index };
        }).filter((layer: { geometry: THREE.BufferGeometry; color: string; index: number } | null): layer is { geometry: THREE.BufferGeometry; color: string; index: number } => layer !== null);
    }

    // 2. Single Profile Case (Legacy/Simple)
    // All transforms MUST be baked into the geometry: the JSX below
    // consumes `resultMesh.geometry` only (no mesh position/rotation),
    // and CSGHelper.subtract() early-returns the untouched target mesh
    // when the wall has no openings — a mesh-level transform would be
    // silently discarded and an unjoined wall would render as an
    // unrotated box pinned to the world origin.
    let baseGeo: THREE.BufferGeometry;

    // Read-time guard (single-profile): stored documents may carry a
    // crossed footprint from before the solver-side sanitization — it is
    // rebuilt from the location line, and unusable polygons fall through
    // to the plain box below so a bad profile can never render.
    const safeProfile = geometryData.profile
        ? sanitizeWallProfileQuad(geometryData.profile as number[][], geometryData.start, geometryData.end)
        : null;

    if (safeProfile) {
        const shape = new THREE.Shape();
        const p = safeProfile;
        shape.moveTo(p[0][0], p[0][1]);
        for (let i = 1; i < p.length; i++) {
            shape.lineTo(p[i][0], p[i][1]);
        }
        shape.closePath();
        baseGeo = new THREE.ExtrudeGeometry(shape, { depth: renderHeight, bevelEnabled: false });
        // Profile coords are already world XY — only the base offset is a transform.
        baseGeo.translate(0, 0, baseZ);
    } else {
        // Basic Box: rotate around Z, then move to the wall center
        // (same order as the discarded mesh rotation + position).
        baseGeo = new THREE.BoxGeometry(length, thickness, renderHeight);
        const center = start.clone().add(end).multiplyScalar(0.5);
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        baseGeo.rotateZ(angle);
        baseGeo.translate(center.x, center.y, baseZ + renderHeight / 2);
    }

    const baseMesh = new THREE.Mesh(baseGeo);
    baseMesh.updateMatrix();

    const resultMesh = CSGHelper.subtract(baseMesh, openingMeshes);

    return [{ geometry: resultMesh.geometry, color: '#e0e0e0', index: 0 }];
}

// ---------------------------------------------------------------------------
// Persistent cache
// ---------------------------------------------------------------------------

/**
 * Bounded result pool. Outer key: the wall's committed `geometryData` object
 * (replace-on-write identity). Inner key: cut-plane height (3D builds use
 * '3d'). Entries validate against a canonical opening-id key so rebuilt
 * opening arrays (same content, new identity after a remount) still hit.
 */
const wallLayersCache = new Map<object, Map<string, {
    validationKey: string;
    layers: WallLayerMesh[];
    lastUsed: number;
}>>();

/** Cap on cached results across the session; oldest entries are disposed. */
const WALL_LAYERS_CACHE_CAP = 512;

let cacheClock = 0;
let cacheEntryCount = 0;

function disposeLayers(layers: WallLayerMesh[] | undefined): void {
    if (!layers) return;
    layers.forEach((layer) => {
        const geo = layer.geometry as (THREE.BufferGeometry & { disposeBoundsTree?: () => void }) | undefined;
        geo?.disposeBoundsTree?.();
        geo?.dispose();
    });
}

function evictOverflow(): void {
    while (cacheEntryCount > WALL_LAYERS_CACHE_CAP) {
        const oldest = wallLayersCache.keys().next();
        if (oldest.done) return;
        const inner = wallLayersCache.get(oldest.value);
        wallLayersCache.delete(oldest.value);
        if (!inner) continue;
        cacheEntryCount -= inner.size;
        inner.forEach((entry) => disposeLayers(entry.layers));
    }
}

/** Canonical, identity-independent key for the opening id lists. */
function openingValidationKey(openings: BIMElement[], parametricOpenings: BIMElement[]): string {
    // Length check short-circuits the common no-openings case without a join.
    if (openings.length === 0 && parametricOpenings.length === 0) return '';
    return `${openings.map((e) => e.id).join(',')}|${parametricOpenings.map((e) => e.id).join(',')}`;
}

/**
 * Cache-aware entry point used by WallRenderer (and the idle warm-up).
 * Returns a cached result when the geometry identity, cut plane and opening
 * ids are unchanged; otherwise builds once and stores.
 */
export function getWallLayerMeshes(
    geometryData: any,
    openings: BIMElement[],
    parametricOpenings: BIMElement[],
    cutPlaneZ: number | null,
    project: MaterialProjectSlice,
): WallLayersResult | null {
    const cutKey = cutPlaneZ === null ? '3d' : String(cutPlaneZ);
    const validationKey = openingValidationKey(openings, parametricOpenings);

    let inner = wallLayersCache.get(geometryData as object);
    const entry = inner?.get(cutKey);
    if (inner && entry && entry.validationKey === validationKey) {
        // Refresh recency (Map insertion order) without touching the result.
        inner.delete(cutKey);
        entry.lastUsed = ++cacheClock;
        inner.set(cutKey, entry);
        return { layers: entry.layers, cacheOwned: true };
    }

    const layers = buildWallLayerMeshes(geometryData, openings, parametricOpenings, cutPlaneZ, project);
    if (!layers) return null;

    if (!inner) {
        inner = new Map();
        wallLayersCache.set(geometryData as object, inner);
    }
    if (entry) {
        // Same geometry + cut plane but the openings changed: the superseded
        // build is unreachable, dispose it now.
        disposeLayers(entry.layers);
    } else {
        cacheEntryCount++;
    }
    inner.set(cutKey, { validationKey, layers, lastUsed: ++cacheClock });
    evictOverflow();
    return { layers, cacheOwned: true };
}

/**
 * Uncached build for per-frame churn (live wall shape-handle drag): results
 * are never pooled, so the renderer's own disposal effect owns them.
 */
export function buildWallLayersUncached(
    geometryData: any,
    openings: BIMElement[],
    parametricOpenings: BIMElement[],
    cutPlaneZ: number | null,
    project: MaterialProjectSlice,
): WallLayersResult | null {
    const layers = buildWallLayerMeshes(geometryData, openings, parametricOpenings, cutPlaneZ, project);
    if (!layers) return null;
    return { layers, cacheOwned: false };
}

/**
 * Drops (and disposes) every cached result for a wall's previous committed
 * geometry. Called by WallRenderer when the committed geometry identity is
 * replaced (wall edited) — the old key can never be looked up again.
 */
export function releaseWallGeometry(geometryData: object | undefined | null): void {
    if (!geometryData) return;
    const inner = wallLayersCache.get(geometryData);
    if (!inner) return;
    wallLayersCache.delete(geometryData);
    cacheEntryCount -= inner.size;
    inner.forEach((entry) => disposeLayers(entry.layers));
}

// ---------------------------------------------------------------------------
// Idle warm-up
// ---------------------------------------------------------------------------

/** Per-tick main-thread budget so warm-up never janks interaction. */
const WARMUP_BUDGET_MS = 6;
/** Pause between ticks: warm-up spreads over idle time, never blocks input. */
const WARMUP_TICK_DELAY_MS = 150;
/** Initial delay so a mode/view switch settles before warming starts. */
const WARMUP_START_DELAY_MS = 300;

/**
 * Progressively pre-builds wall body geometries for the instanced bulk while
 * a shaded-family 3D view is active. The instanced batches cover these walls,
 * so the work runs in idle time with a small per-tick budget; when the user
 * switches to an edge-family mode, every individual wall renderer then finds
 * its geometry pre-built and the switch paints on the next frame.
 */
export function useWallCacheWarmup(
    enabled: boolean,
    groups: BIMInstanceGroup[],
    getElementsMap: () => Map<string, BIMElement>,
): void {
    // Latest-ref pattern: the elements map changes on every project mutation
    // but must not restart the warm-up; jobs read the freshest map per tick.
    const getElementsRef = useRef(getElementsMap);
    getElementsRef.current = getElementsMap;

    useEffect(() => {
        if (!enabled) return;

        const index = getOpeningIndex(getElementsRef.current());
        const jobs: { geometryData: any; openings: BIMElement[]; parametricOpenings: BIMElement[] }[] = [];
        groups.forEach((group) => {
            if (group.instanceType !== 'wall') return;
            group.ids.forEach((id) => {
                const element = group.elementsById.get(id);
                if (!element) return;
                const g = (element as any).geometryData;
                if (!g || !g.start || !g.end) return;
                jobs.push({
                    geometryData: g,
                    openings: index.hostedByWall.get(id) ?? EMPTY_OPENINGS,
                    parametricOpenings: index.byStructuralHost.get(id) ?? EMPTY_OPENINGS,
                });
            });
        });
        if (jobs.length === 0) return;

        let cancelled = false;
        let timer = 0;
        let cursor = 0;

        const step = () => {
            if (cancelled) return;
            const project = useBIMStore.getState().project;
            const startedAt = performance.now();
            while (cursor < jobs.length && performance.now() - startedAt < WARMUP_BUDGET_MS) {
                const job = jobs[cursor++];
                getWallLayerMeshes(job.geometryData, job.openings, job.parametricOpenings, null, project);
            }
            if (cursor < jobs.length) {
                timer = window.setTimeout(step, WARMUP_TICK_DELAY_MS);
            }
        };

        timer = window.setTimeout(step, WARMUP_START_DELAY_MS);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [enabled, groups]);
}
